import {
    contractConverter,
    Contract,
    CONTRACT_COLLECTION_NAME,
    translationCoverter,
    TRANSLATIONS_COLLECTION_NAME,
    Translation,
} from 'models';
import { NavLink } from 'react-router-dom';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import './dashboard.css';
import { Button } from 'components/button';

const radius = 62.5;
const strokeStyles = (total: number, complete: number) => {
    if (complete <= total / 2)
        return {
            strokeDasharray: `${(((2 * 22) / 7) * radius * complete) / total} ${
                (((2 * 22) / 7) * radius * (total - complete)) / total
            }`,
        };
    return {
        strokeDasharray: `${((2 * 22) / 7) * radius * (complete / total)}`,
    };
};

const getCompletionInfo = (contract: Contract, translationsMap: Map<string, Translation>) => {
    if (contract.markets.length) {
        const indexIds = contract.getIndexIds();
        const districtLanguages = Array.from(
            new Set(contract.markets.map((market) => market.languageIndexId))
        );
        const languageCompletion = districtLanguages.map((languageIndexId) => {
            return {
                languageIndexId,
                count: indexIds
                    .map(
                        (indexId) =>
                            !!translationsMap.get(
                                `${languageIndexId}-${indexId.split('_').splice(-1)[0]}`
                            )?.content
                    )
                    .filter((i) => i).length,
            };
        });
        const complete = languageCompletion.reduce((c, a) => c + a.count, 0);
        const total = indexIds.length * (districtLanguages.length || 1);
        const message = `Languages: ${districtLanguages.length}`;
        return { complete, total, message };
    }
    const complete = contract.contractSections.filter((contractSection) =>
        contractSection.segments.find(
            (segment) => segment.clauseIndexId || segment.getClauses().length
        )
    ).length;
    const total = contract.contractSections.length;
    const message = `Sections: ${complete}/${contract.contractSections.length}`;
    return { complete, total, message };
};

const ContractItem = ({ contract }: { contract: Contract }) => {
    const [, , , translationsMap] = useCollectionContext(
        TRANSLATIONS_COLLECTION_NAME
    ) as CollectionState<Translation>;
    const { complete, total, message } = getCompletionInfo(contract, translationsMap);
    return (
        <NavLink
            className="contractItem"
            to={`/contract-languages/${encodeURIComponent(contract.id)}`}
            style={{ color: 'inherit', textDecoration: 'inherit' }}
        >
            <div
                style={{
                    height: '50px',
                    padding: '18px 0 10px 0',

                    fontSize: '18px',
                }}
            >
                {contract.name}
            </div>
            <div
                style={{
                    height: '150px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}
            >
                <div style={{ fontSize: '34px', margin: '0 0 1px 11px' }}>
                    {total ? Math.round((complete / total) * 100) : 0}
                </div>
                <div style={{ marginTop: '10px' }}>%</div>
                <div
                    style={{
                        height: '150px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        zIndex: 1,
                    }}
                >
                    <svg
                        width="129"
                        height="129"
                        viewBox="0 0 129 129"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle
                            cx={`${radius + 2}`}
                            cy={`${radius + 2}`}
                            r={`${radius}`}
                            transform="rotate(-90 64.5 64.5)"
                            stroke="#E9E9E9"
                            strokeWidth="4"
                        />
                    </svg>
                </div>
                <div
                    style={{
                        transform: complete <= total / 2 ? 'rotate(-90deg)' : 'rotate(-90deg)',
                        height: '150px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        zIndex: 1,
                    }}
                >
                    <svg
                        width="145"
                        height="145"
                        viewBox="-9 -9 145 145"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        {total && complete ? (
                            <circle
                                cx={`${radius + 1}`}
                                cy={`${radius + 1}`}
                                r={`${radius}`}
                                strokeWidth="14"
                                stroke="url(#paint0_radial_1_516)"
                                style={{
                                    ...strokeStyles(total, complete),
                                    strokeLinecap: 'round',
                                    transition: 'all 0.67s',
                                }}
                            />
                        ) : null}

                        <defs>
                            <radialGradient
                                id="paint0_radial_1_516"
                                cx="0"
                                cy="0"
                                r="1"
                                gradientUnits="userSpaceOnUse"
                                gradientTransform="translate(19 152) rotate(-58.6769) scale(191.396)"
                            >
                                <stop stopColor="#ADD9FD" />
                                <stop offset="1" stopColor="#0965A6" />
                            </radialGradient>
                        </defs>
                    </svg>
                </div>
            </div>
            {message}
        </NavLink>
    );
};

export const array_chunks = (array: any[], chunk_size: number) =>
    Array(Math.ceil(array.length / chunk_size))
        .fill(undefined)
        .map((_, index) => index * chunk_size)
        .map((begin) => array.slice(begin, begin + chunk_size));

export const List = () => {
    const [contracts, pending] = useCollectionContext(
        CONTRACT_COLLECTION_NAME
    ) as CollectionState<Contract>;
    if (pending || !contracts?.length) {
        return null;
    }
    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                margin: '40px 0',
            }}
        >
            {array_chunks(contracts, 5).map((contracts, i) => (
                <div className="row" key={i}>
                    {contracts.map((contract) => (
                        <ContractItem key={contract.id} contract={contract} />
                    ))}
                </div>
            ))}
        </div>
    );
};

export function Dashboard() {
    return (
        <div className="dashboard">
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '40px',
                }}
            >
                <div style={{ flexBasis: '200px' }}></div>
                <div>
                    <div
                        className="dashboardTitle"
                        style={{ fontSize: '34px', textAlign: 'center' }}
                    >
                        Dashboard
                    </div>
                    <div className="dashboardTitleText">Here you can check your projects</div>
                </div>
                <div style={{ flexBasis: '200px', display: 'flex', alignItems: 'center' }}>
                    <NavLink
                        style={{
                            height: '50px',
                            padding: '18px 0 10px 0',
                            color: 'inherit',
                            textDecoration: 'inherit',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        to={`/contract`}
                    >
                        <Button style={{ width: '186px' }}>Create new document</Button>
                    </NavLink>
                </div>
            </div>

            <div className="contractsDisplay">
                <CollectionProvider
                    collectionName={CONTRACT_COLLECTION_NAME}
                    converter={contractConverter}
                >
                    <CollectionProvider
                        collectionName={TRANSLATIONS_COLLECTION_NAME}
                        converter={translationCoverter}
                        keyFunction={(translation) =>
                            `${translation.languageIndexId}-${translation.targetId}`
                        }
                    >
                        <List />
                    </CollectionProvider>
                </CollectionProvider>
            </div>
        </div>
    );
}
